// Admin "Content Engine" tab — hooks, scripter, pipeline, trending, ideator, performance brief, competitors.
// MongoDB/GPT port of the course lessons (see each server file's header). Data: routes/adminContentEngine.js.
import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";

const num = (v) => (v == null ? "—" : Number(v).toLocaleString());
const compact = (v) => Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(v || 0);
const day = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const errMsg = (e, fallback) => e.response?.data?.error || fallback;

const NAV = [
  ["prompt", "Prompt"], ["pipeline", "Pipeline"], ["hooks", "Hooks"], ["scripter", "Scripter"], ["trending", "Trending"],
  ["ideator", "Ideator"], ["brief", "Performance Brief"], ["competitors", "Competitors"],
];
const NAV_KEY = "admindash.contentEngine.view";

export default function ContentEngineTab() {
  const [view, setView] = useState(() => { try { return localStorage.getItem(NAV_KEY) || "pipeline"; } catch { return "pipeline"; } });
  const setViewPersist = (v) => { setView(v); try { localStorage.setItem(NAV_KEY, v); } catch { /* no-op */ } };
  // Handoff state: "Script it" on a hook/pipeline item jumps to the Scripter view pre-filled.
  const [scripterSeed, setScripterSeed] = useState(null);
  const goScript = (seed) => { setScripterSeed(seed); setViewPersist("scripter"); };

  return (
    <div className="admindash-section admindash-fade-in">
      <div className="ce-nav">
        {NAV.map(([id, label]) => (
          <button key={id} className={`ce-nav-btn ${view === id ? "on" : ""}`} onClick={() => setViewPersist(id)}>{label}</button>
        ))}
      </div>
      {view === "prompt" && <PromptView />}
      {view === "pipeline" && <PipelineView onScript={goScript} />}
      {view === "hooks" && <HooksView onScript={goScript} />}
      {view === "scripter" && <ScripterView seed={scripterSeed} clearSeed={() => setScripterSeed(null)} />}
      {view === "trending" && <TrendingView />}
      {view === "ideator" && <IdeatorView />}
      {view === "brief" && <BriefView />}
      {view === "competitors" && <CompetitorsView onScript={goScript} />}
    </div>
  );
}

/* ───────────────────────── Pipeline ───────────────────────── */

const STATUS_LABELS = { idea: "💡 Idea", researched: "📝 Researched", scripting: "✍️ Scripting", filming: "🎬 Filming", editing: "✂️ Editing", scheduled: "📅 Scheduled", posted: "✅ Posted", archived: "🗄️ Archived" };

function PipelineView({ onScript }) {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const load = useCallback(() => {
    axiosInstance.get("/admin/content-engine/pipeline", { params: statusFilter ? { status: statusFilter } : {} })
      .then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e, "Failed to load pipeline")); setData({ items: [], counts: {}, statuses: [] }); });
  }, [statusFilter]);
  useEffect(load, [load]);

  const addIdea = async () => {
    if (!newTitle.trim()) return;
    try { await axiosInstance.post("/admin/content-engine/pipeline", { title: newTitle.trim() }); setNewTitle(""); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't add idea")); }
  };
  const setStatus = async (id, status) => {
    try { await axiosInstance.patch(`/admin/content-engine/pipeline/${id}`, { status }); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't update status")); }
  };
  const remove = async (id) => {
    try { await axiosInstance.delete(`/admin/content-engine/pipeline/${id}`); load(); if (open === id) setOpen(null); }
    catch (e) { toast.error(errMsg(e, "Couldn't delete")); }
  };

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;
  const { items, counts, statuses } = data;

  return (
    <div>
      <div className="ce-row-flex">
        <input className="ce-input" placeholder="New idea title…" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addIdea()} />
        <button className="admindash-refresh" onClick={addIdea}>+ Add idea</button>
      </div>
      <div className="ce-chips">
        <button className={`igc-chip ${!statusFilter ? "on" : ""}`} onClick={() => setStatusFilter("")}>All <span className="muted">{Object.values(counts).reduce((a, b) => a + b, 0)}</span></button>
        {statuses.map((s) => (
          <button key={s} className={`igc-chip ${statusFilter === s ? "on" : ""}`} onClick={() => setStatusFilter(s)}>{STATUS_LABELS[s] || s} <span className="muted">{counts[s] || 0}</span></button>
        ))}
      </div>
      {items.length === 0 ? <div className="admindash-empty">Nothing here yet. Add an idea, or pull one in from Trending.</div> : (
        <div className="ce-list">
          {items.map((it) => (
            <div key={it._id}>
              <div className="ce-card" onClick={() => setOpen(open === it._id ? null : it._id)}>
                <div className="ce-card-main">
                  <div className="ce-card-title">{it.title}</div>
                  <div className="muted ce-card-meta">{STATUS_LABELS[it.status]} · {it.topic || "no topic"} · {day(it.createdAt)} {it.source !== "manual" && <span className="ce-tag">{it.source}</span>}</div>
                </div>
                <span className="igc-more">{open === it._id ? "▴" : "▾"}</span>
              </div>
              {open === it._id && (
                <div className="ce-detail" onClick={(e) => e.stopPropagation()}>
                  <div className="ce-detail-row">
                    <label className="muted">Status</label>
                    <select value={it.status} onChange={(e) => setStatus(it._id, e.target.value)}>{statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}</select>
                    <button className="ce-x" onClick={() => remove(it._id)}>Delete</button>
                  </div>
                  {it.materialsResearch && <div className="ce-field"><span className="muted">Research</span><p>{it.materialsResearch}</p></div>}
                  {it.contentAngle && <div className="ce-field"><span className="muted">Angle</span><p>{it.contentAngle}</p></div>}
                  {it.hook1 && <div className="ce-field"><span className="muted">Hook</span><p>{it.hook1}</p></div>}
                  {it.script && <details className="ce-field"><summary className="muted">Script</summary><pre className="ce-pre">{it.script}</pre></details>}
                  {it.postCaption && <div className="ce-field"><span className="muted">Caption</span><p>{it.postCaption}</p></div>}
                  <div className="ce-actions">
                    <button className="igc-chip" onClick={() => onScript({ pipelineItemId: it._id, topic: it.topic || it.title, hook: it.hook1 || "", framework: it.hookSource || "", research: it.materialsResearch || "", contentType: it.contentTypeTag || "" })}>Script it →</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Hooks ───────────────────────── */

function HookRow({ h, onScript }) {
  return (
    <div className="ce-hookrow">
      <div className="ce-hook-text">
        <p>"{h.spokenHook || h.hook}"</p>
        {(h.spokenHookFramework || h.framework) && <div className="muted ce-hook-fw">{h.spokenHookFramework || h.framework}</div>}
      </div>
      <div className="ce-hook-meta">
        <span className="igc-badge">{h.spokenHookStructure || h.structure || "—"}</span>
        <span className="muted">{compact(h.views ?? h.originalViews)} views</span>
        {h.source && <span className="ce-tag">{h.source}</span>}
      </div>
      {onScript && <button className="igc-chip" onClick={() => onScript({ topic: "", hook: h.spokenHook || h.hook, framework: h.spokenHookFramework || h.framework })}>Script it →</button>}
    </div>
  );
}

function HooksView({ onScript }) {
  const [q, setQ] = useState(""); const [structure, setStructure] = useState(""); const [source, setSource] = useState("");
  const [data, setData] = useState(null);
  const [topic, setTopic] = useState(""); const [contentType, setContentType] = useState("");
  const [generated, setGenerated] = useState(null); const [generating, setGenerating] = useState(false);

  const search = useCallback(() => {
    axiosInstance.get("/admin/content-engine/hooks", { params: { q: q || undefined, structure: structure || undefined, source: source || undefined } })
      .then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e, "Search failed")); setData({ kallaway: [], noe: [], totals: {} }); });
  }, [q, structure, source]);
  useEffect(search, [search]);

  const generate = async () => {
    if (!topic.trim()) return toast("Enter a topic first");
    setGenerating(true);
    try { setGenerated((await axiosInstance.post("/admin/content-engine/hooks/generate", { topic, contentType: contentType || undefined })).data); }
    catch (e) { toast.error(errMsg(e, "Couldn't generate hooks")); }
    setGenerating(false);
  };

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;

  return (
    <div>
      <div className="ce-panel">
        <div className="ce-panel-title">Generate 6 hooks for a topic</div>
        <div className="ce-row-flex">
          <input className="ce-input" placeholder="Topic (e.g. streaming for small creators)" value={topic} onChange={(e) => setTopic(e.target.value)} onKeyDown={(e) => e.key === "Enter" && generate()} />
          <select value={contentType} onChange={(e) => setContentType(e.target.value)}><option value="">Any content type</option><option>Demo</option><option>Tutorial</option><option>Everything Else</option></select>
          <button className={`admindash-refresh ${generating ? "igc-btn-busy" : ""}`} onClick={generate} disabled={generating}>{generating ? <><span className="igc-spin igc-spin--light" />Generating…</> : "Generate 6 hooks"}</button>
        </div>
        {generated && (
          <div className="ce-hooklist">
            {generated.hooks.map((h, i) => (
              <div key={i}>
                <HookRow h={h} onScript={onScript} />
                {h.whyItWorks && <div className="muted ce-why">{h.whyItWorks}{h.originalHook && h.originalHook !== h.hook && <> — adapted from: "{h.originalHook.slice(0, 80)}{h.originalHook.length > 80 ? "…" : ""}"</>}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ce-panel-title" style={{ marginTop: 18 }}>Browse the hook database <span className="muted">({data.totals.kallaway} Kallaway · {data.totals.noe} Noe)</span></div>
      <div className="ce-row-flex">
        <input className="ce-input" placeholder="Search hooks…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={source} onChange={(e) => setSource(e.target.value)}><option value="">All sources</option><option value="kallaway">Kallaway</option><option value="noe">Noe</option></select>
        <select value={structure} onChange={(e) => setStructure(e.target.value)}><option value="">All structures</option>{["Secret Reveal", "Educational", "Contrarian", "Raw Shock", "Question", "Comparison", "Experimentation"].map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      {data.totals.kallaway === 0 && data.totals.noe === 0 && <div className="igc-banner">No hooks loaded yet — run <code>npm run seed:hooks</code> on the server once.</div>}
      <div className="ce-hooklist">
        {[...data.kallaway.map((h) => ({ ...h, source: "kallaway" })), ...data.noe.map((h) => ({ ...h, source: "noe" }))].slice(0, 60).map((h, i) => <HookRow key={i} h={h} onScript={onScript} />)}
      </div>
    </div>
  );
}

/* ───────────────────────── Scripter ───────────────────────── */

// Shared with PromptView's /script and /clipbait commands, so a filming card looks the same everywhere.
function FilmingCard({ result }) {
  return (
    <div className="ce-fc-wrap">
      <div className="ce-fc-hook">HOOK: "{result.hook}"</div>
      {result.card.beats.map((b, i) => (
        <div key={i} className="ce-fc-beat">
          {b.screenDirection && <div className="ce-fc-screen">[SCREEN] {b.screenDirection}</div>}
          <div className="muted ce-fc-fw">{b.framework}</div>
          <p>"{b.example}"</p>
        </div>
      ))}
      <div className="ce-fc-close">CLOSE: "{result.card.close}"</div>
      <div className="ce-fc-grid">
        {result.card.onScreenTextHook && <div><span className="muted">On-screen text</span><p>{result.card.onScreenTextHook}</p></div>}
        <div><span className="muted">Visual format</span><p>{result.card.visualFormat}</p></div>
        {result.card.postCaption && <div><span className="muted">Post caption</span><p>{result.card.postCaption}</p></div>}
        {result.card.recordingInstructions && <div><span className="muted">Recording notes</span><p>{result.card.recordingInstructions}</p></div>}
      </div>
    </div>
  );
}

function ScripterView({ seed, clearSeed }) {
  const [topic, setTopic] = useState(seed?.topic || ""); const [hook, setHook] = useState(seed?.hook || "");
  const [framework, setFramework] = useState(seed?.framework || ""); const [contentType, setContentType] = useState(seed?.contentType || "Everything Else");
  const [research, setResearch] = useState(seed?.research || ""); const [pipelineItemId] = useState(seed?.pipelineItemId || null);
  const [result, setResult] = useState(null); const [busy, setBusy] = useState(false);

  useEffect(() => { if (seed) { setTopic(seed.topic || ""); setHook(seed.hook || ""); setFramework(seed.framework || ""); setContentType(seed.contentType || "Everything Else"); setResearch(seed.research || ""); clearSeed(); } }, [seed, clearSeed]); // eslint-disable-line

  const generate = async () => {
    if (!topic.trim() || !hook.trim()) return toast("Topic and a chosen hook are both required");
    setBusy(true);
    try { setResult((await axiosInstance.post("/admin/content-engine/scripter", { topic, hook, framework, contentType, research, pipelineItemId })).data); if (pipelineItemId) toast.success("Saved to the pipeline item"); }
    catch (e) { toast.error(errMsg(e, "Couldn't generate a filming card")); }
    setBusy(false);
  };

  return (
    <div>
      <div className="ce-panel">
        <div className="ce-panel-title">Generate a filming card {pipelineItemId && <span className="muted">— will save to the pipeline item</span>}</div>
        <input className="ce-input ce-input--full" placeholder="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} />
        <textarea className="ce-input ce-input--full ce-textarea" placeholder="Chosen hook (spoken line)" value={hook} onChange={(e) => setHook(e.target.value)} rows={2} />
        <div className="ce-row-flex">
          <input className="ce-input" placeholder="Hook framework (optional)" value={framework} onChange={(e) => setFramework(e.target.value)} />
          <select value={contentType} onChange={(e) => setContentType(e.target.value)}><option>Demo</option><option>Tutorial</option><option>Everything Else</option></select>
        </div>
        <textarea className="ce-input ce-input--full ce-textarea" placeholder="Research / facts to use (optional)" value={research} onChange={(e) => setResearch(e.target.value)} rows={2} />
        <button className={`admindash-refresh ${busy ? "igc-btn-busy" : ""}`} onClick={generate} disabled={busy}>{busy ? <><span className="igc-spin igc-spin--light" />Writing…</> : "Generate filming card"}</button>
      </div>

      {result && <FilmingCard result={result} />}
    </div>
  );
}

/* ───────────────────────── Trending ───────────────────────── */

function TrendingView() {
  const [logs, setLogs] = useState(null); const [running, setRunning] = useState(false);
  const load = useCallback(() => { axiosInstance.get("/admin/content-engine/trending/logs").then((r) => setLogs(r.data.logs)).catch(() => setLogs([])); }, []);
  useEffect(load, [load]);

  const run = async () => {
    setRunning(true);
    try { await axiosInstance.post("/admin/content-engine/trending/run", {}); toast.success("Research complete"); load(); }
    catch (e) { toast.error(errMsg(e, "Research run failed")); }
    setRunning(false);
  };
  const addToPipeline = async (date, index) => {
    try { await axiosInstance.post(`/admin/content-engine/trending/${date}/${index}/add-to-pipeline`); toast.success("Added to pipeline"); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't add — maybe already added")); }
  };

  if (!logs) return <div className="admindash-empty admindash-shimmer">Loading…</div>;
  return (
    <div>
      <div className="ce-row-flex">
        <button className={`admindash-refresh ${running ? "igc-btn-busy" : ""}`} onClick={run} disabled={running}>{running ? <><span className="igc-spin igc-spin--light" />Researching (Reddit + X)…</> : "Run today's research"}</button>
        <span className="muted">Reddit + X/Twitter (via your xAI key). General news search isn't wired up (needs a paid search API).</span>
      </div>
      {logs.length === 0 ? <div className="admindash-empty">No research runs yet.</div> : logs.map((log) => (
        <div key={log.date} style={{ marginTop: 16 }}>
          <div className="ce-panel-title">{log.date} <span className="muted">— {log.sourcesScraped?.reddit || 0} Reddit, {log.sourcesScraped?.x || 0} X posts scraped</span></div>
          <div className="ce-list">
            {log.topics.map((t, i) => (
              <div key={i} className="ce-card ce-card--static">
                <div className="ce-card-main">
                  <div className="ce-card-title">{t.title} {t.score != null && <span className="igc-badge">{t.score}/100</span>}</div>
                  <div className="muted">{t.why}</div>
                  {t.angle && <div className="ce-hook-fw">Angle: {t.angle}</div>}
                  {t.sourceUrl && <a href={t.sourceUrl} target="_blank" rel="noreferrer" className="ce-tag">{t.source} ↗</a>}
                </div>
                <button className="igc-chip" disabled={t.addedToPipeline} onClick={() => addToPipeline(log.date, i)}>{t.addedToPipeline ? "Added ✓" : "Add to pipeline"}</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────── Ideator ───────────────────────── */

function IdeatorView() {
  const [title, setTitle] = useState(""); const [topic, setTopic] = useState(""); const [research, setResearch] = useState("");
  const [result, setResult] = useState(null); const [busy, setBusy] = useState(false); const [picked, setPicked] = useState(new Set());

  const generate = async () => {
    if (!title.trim()) return toast("Enter a title/idea first");
    setBusy(true); setPicked(new Set());
    try { setResult((await axiosInstance.post("/admin/content-engine/ideator", { title, topic, existingResearch: research })).data); }
    catch (e) { toast.error(errMsg(e, "Couldn't generate variations")); }
    setBusy(false);
  };
  const toggle = (i) => setPicked((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const save = async () => {
    if (!picked.size) return toast("Pick at least one variation");
    try {
      const variations = [...picked].map((i) => result.variations[i]);
      await axiosInstance.post("/admin/content-engine/ideator/save", { variations, topic: result.topic, materialsResearch: research || undefined });
      toast.success(`Saved ${variations.length} to the pipeline`); setResult(null); setPicked(new Set());
    } catch (e) { toast.error(errMsg(e, "Couldn't save")); }
  };

  return (
    <div className="ce-panel">
      <div className="ce-panel-title">One idea → up to 5 video variations</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>Uses GPT's own knowledge, not live search — good for well-known topics, less reliable for breaking news. Paste facts into "research" for anything time-sensitive.</div>
      <input className="ce-input ce-input--full" placeholder="Idea / title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="ce-row-flex"><input className="ce-input" placeholder="Category (optional)" value={topic} onChange={(e) => setTopic(e.target.value)} /></div>
      <textarea className="ce-input ce-input--full ce-textarea" placeholder="Research / known facts (optional, recommended)" value={research} onChange={(e) => setResearch(e.target.value)} rows={2} />
      <button className={`admindash-refresh ${busy ? "igc-btn-busy" : ""}`} onClick={generate} disabled={busy}>{busy ? <><span className="igc-spin igc-spin--light" />Thinking…</> : "Generate 5 variations"}</button>

      {result && (
        <>
          <div className="ce-hooklist" style={{ marginTop: 14 }}>
            {result.variations.map((v, i) => (
              <label key={i} className={`ce-variation ${picked.has(i) ? "on" : ""}`}>
                <input type="checkbox" checked={picked.has(i)} onChange={() => toggle(i)} />
                <div>
                  <div className="ce-card-title">{v.title}</div>
                  <div className="muted">{v.framing} · {v.contentType} · {v.format} · ~{v.durationSeconds}s</div>
                  <p>{v.description}</p>
                </div>
              </label>
            ))}
          </div>
          <button className="admindash-refresh" onClick={save} disabled={!picked.size} style={{ marginTop: 10 }}>Save {picked.size || ""} picked to pipeline</button>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── Performance Brief ───────────────────────── */

function RankTable({ rows, cols }) {
  if (!rows?.length) return <div className="muted">No data yet.</div>;
  return (
    <table className="ce-table"><thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i}><td>{r.name}{r.smallSample && <span className="ce-tag" title="Fewer than 3 posts — directional, not proven">small n</span>}</td><td>{num(r.videos)}</td><td>{compact(r.avgViews)}</td><td>{r.outlierRate}%</td></tr>
      ))}</tbody>
    </table>
  );
}

function BriefView() {
  const [brief, setBrief] = useState(null); const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); axiosInstance.get("/admin/content-engine/performance-brief").then((r) => setBrief(r.data)).catch((e) => toast.error(errMsg(e, "Failed to load"))).finally(() => setLoading(false)); }, []);
  useEffect(load, [load]);

  if (loading) return <div className="admindash-empty admindash-shimmer">Crunching your Instagram numbers…</div>;
  if (!brief || brief.empty) return <div className="admindash-empty">Not enough analyzed posts yet — analyze some Reels in the Instagram tab first.</div>;

  const s = brief.scripterBrief;
  return (
    <div>
      <div className="admindash-grid">
        <div className="admindash-stat"><div className="admindash-stat-value">{num(brief.overview.totalPosts)}</div><div className="admindash-stat-label">Posts</div></div>
        <div className="admindash-stat"><div className="admindash-stat-value">{compact(brief.overview.avgViews)}</div><div className="admindash-stat-label">Avg views</div></div>
        <div className="admindash-stat"><div className="admindash-stat-value">{brief.overview.outlierRate}%</div><div className="admindash-stat-label">Outlier rate</div></div>
        <div className="admindash-stat"><div className="admindash-stat-value">{num(brief.analyzedPosts)}</div><div className="admindash-stat-label">Analyzed</div></div>
      </div>

      <div className="ce-panel">
        <div className="ce-panel-title">Scripter brief <span className="muted">— feeds Ideator/Scripter automatically</span></div>
        {s.note && <div className="igc-banner">{s.note}</div>}
        <ul className="ce-brief-list">
          {s.bestHookStructure && <li><b>Best hook structure:</b> {s.bestHookStructure.name} ({s.bestHookStructure.outlierRate}% outlier rate, {compact(s.bestHookStructure.avgViews)} avg)</li>}
          {s.worstHookStructure && <li><b>Worst hook structure:</b> {s.worstHookStructure.name} ({s.worstHookStructure.outlierRate}%)</li>}
          {s.bestDuration && <li><b>Best duration:</b> {s.bestDuration.bucket} ({s.bestDuration.outlierRate}%)</li>}
          {s.bestVisualFormat && <li><b>Best visual format:</b> {s.bestVisualFormat.name}</li>}
          {!!s.bestTopics?.length && <li><b>Hot topics:</b> {s.bestTopics.map((t) => t.name).join(", ")}</li>}
          {!!s.deadTopics?.length && <li><b>Dead topics (stop making these):</b> {s.deadTopics.map((t) => t.name).join(", ")}</li>}
          {!!s.recurringTextHookWords?.length && <li><b>Recurring text-hook words:</b> {s.recurringTextHookWords.join(", ")}</li>}
        </ul>
      </div>

      <div className="ce-two-col">
        <div><div className="ce-panel-title">Hook structures</div><RankTable rows={brief.hookStructures} cols={["Structure", "Posts", "Avg views", "Outlier"]} /></div>
        <div><div className="ce-panel-title">Visual formats</div><RankTable rows={brief.visualFormats} cols={["Format", "Posts", "Avg views", "Outlier"]} /></div>
        <div><div className="ce-panel-title">Duration</div><RankTable rows={brief.durationBuckets} cols={["Bucket", "Posts", "Avg views", "Outlier"]} /></div>
        <div><div className="ce-panel-title">Content structure</div><RankTable rows={brief.contentStructures} cols={["Structure", "Posts", "Avg views", "Outlier"]} /></div>
      </div>

      <div className="ce-panel-title" style={{ marginTop: 16 }}>Top hook frameworks</div>
      <div className="ce-hooklist">
        {brief.top20.slice(0, 10).map((h) => (
          <div key={h.mediaId} className="ce-hookrow">
            <div className="ce-hook-text"><p>"{h.spokenHook}"</p>{h.framework && <div className="muted ce-hook-fw">{h.framework}</div>}</div>
            <div className="ce-hook-meta"><span className="igc-badge">{h.hookStructure || "—"}</span><span className="muted">{compact(h.views)} views · {h.outlierScore}×</span></div>
            <a className="igc-chip" href={h.url} target="_blank" rel="noreferrer">View ↗</a>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────── Competitors ───────────────────────── */

const rel = (d) => {
  if (!d) return null;
  const h = Math.floor((Date.now() - new Date(d).getTime()) / 3600000);
  if (h < 1) return "just now"; if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`;
};

// Small reusable log view for the two background jobs on this tab (scrape / analyze) — same visual
// language as the Instagram tab's analysis log (igc-log classes), simplified: no per-step checklist,
// just "N of M" + a scrolling, timestamped, color-coded line log so a stall or failure is never silent.
function JobLog({ job, label }) {
  if (!job?.total) return null;
  const pct = job.total ? Math.round(((job.done + job.failed) / job.total) * 100) : 0;
  return (
    <div className={`ce-panel ${job.running ? "" : "ce-panel--done"}`} style={{ marginTop: 10 }}>
      <div className="ce-panel-title">
        {job.running ? <><span className="igc-spin" />{label} {job.current ? `@${job.current}` : ""} — {job.done + job.failed}/{job.total}</> : `${label} finished — ${job.done} done${job.failed ? `, ${job.failed} failed` : ""}`}
      </div>
      {job.running && <div className="igc-bar" style={{ marginBottom: 10 }}><div className="igc-bar-fill igc-bar-fill--live igc-bar-fill--run" style={{ width: `${Math.max(6, pct)}%` }} /></div>}
      <div className="igc-log">
        {(job.log || []).map((l, i) => <div key={i} className={`igc-log-line igc-log--${l.level || "info"}`}><span className="igc-log-t">{new Date(l.t).toLocaleTimeString([], { hour12: false })}</span>{l.msg}</div>)}
      </div>
    </div>
  );
}

function CompetitorsView({ onScript }) {
  const [competitors, setCompetitors] = useState(null); const [apifyConfigured, setApifyConfigured] = useState(false);
  const [handle, setHandle] = useState(""); const [analysis, setAnalysis] = useState(null);
  const [scrapeJob, setScrapeJob] = useState(null); const [analyzeJob, setAnalyzeJob] = useState(null);
  const [adapting, setAdapting] = useState(null); // index of the hook currently being adapted for Clipbait

  const scriptForClipbait = async (h, i) => {
    setAdapting(i);
    try {
      const r = (await axiosInstance.post("/admin/content-engine/hooks/adapt-for-clipbait", { hook: h.spokenHook, framework: h.hookFramework || "" })).data;
      onScript({ topic: r.topic, hook: r.hook, framework: r.framework || "", research: r.research });
    } catch (e) {
      toast.error(errMsg(e, "Couldn't adapt this hook"));
    }
    setAdapting(null);
  };

  const load = useCallback(() => {
    axiosInstance.get("/admin/content-engine/competitors").then((r) => { setCompetitors(r.data.competitors); setApifyConfigured(r.data.apifyConfigured); }).catch(() => setCompetitors([]));
    axiosInstance.get("/admin/content-engine/competitors/analysis").then((r) => setAnalysis(r.data)).catch(() => setAnalysis({ empty: true }));
  }, []);
  useEffect(load, [load]);
  // Pick up a job already running (e.g. this page was reloaded mid-scrape) and poll while either runs.
  useEffect(() => {
    axiosInstance.get("/admin/content-engine/competitors/scrape/status").then((r) => setScrapeJob(r.data)).catch(() => {});
    axiosInstance.get("/admin/content-engine/competitors/analyze-posts/status").then((r) => setAnalyzeJob(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!scrapeJob?.running && !analyzeJob?.running) return undefined;
    const t = setInterval(async () => {
      if (scrapeJob?.running) { const r = (await axiosInstance.get("/admin/content-engine/competitors/scrape/status")).data; setScrapeJob(r); if (!r.running) load(); }
      if (analyzeJob?.running) { const r = (await axiosInstance.get("/admin/content-engine/competitors/analyze-posts/status")).data; setAnalyzeJob(r); if (!r.running) load(); }
    }, 2000);
    return () => clearInterval(t);
  }, [scrapeJob?.running, analyzeJob?.running, load]);

  const add = async () => {
    if (!handle.trim()) return;
    try { await axiosInstance.post("/admin/content-engine/competitors", { handle: handle.trim() }); setHandle(""); load(); }
    catch (e) { toast.error(errMsg(e, "Couldn't add competitor")); }
  };
  const toggleActive = async (c) => { try { await axiosInstance.patch(`/admin/content-engine/competitors/${c._id}`, { isActive: !c.isActive }); load(); } catch (e) { toast.error(errMsg(e, "Failed")); } };
  const remove = async (id) => { try { await axiosInstance.delete(`/admin/content-engine/competitors/${id}`); load(); } catch (e) { toast.error(errMsg(e, "Failed")); } };
  const scrapeAll = async () => {
    try {
      const r = (await axiosInstance.post("/admin/content-engine/competitors/scrape")).data;
      if (!r.started) return toast(r.total === 0 ? "No active competitors to scrape" : "Nothing to do");
      setScrapeJob({ running: true, total: r.total, done: 0, failed: 0, log: [] });
      toast(`Scraping ${r.total} competitor(s) — this runs in the background, feel free to switch tabs`, { icon: "🔎" });
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.status) { setScrapeJob(e.response.data.status); return toast("A scrape is already running"); }
      toast.error(errMsg(e, "Scrape failed"));
    }
  };
  const analyzePosts = async () => {
    try {
      const r = (await axiosInstance.post("/admin/content-engine/competitors/analyze-posts", { limit: 10 })).data;
      if (!r.started) return toast("Nothing to analyze");
      setAnalyzeJob({ running: true, total: r.total, done: 0, failed: 0, log: [] });
      toast(`Analyzing ${r.total} post(s) — this runs in the background`, { icon: "🎬" });
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.status) { setAnalyzeJob(e.response.data.status); return toast("An analysis is already running"); }
      toast.error(errMsg(e, "Analyze failed"));
    }
  };
  const scraping = !!scrapeJob?.running, analyzing = !!analyzeJob?.running;

  if (!competitors) return <div className="admindash-empty admindash-shimmer">Loading…</div>;

  return (
    <div>
      <div className="ce-row-flex">
        <input className="ce-input" placeholder="@competitor_handle" value={handle} onChange={(e) => setHandle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <button className="admindash-refresh" onClick={add}>+ Track</button>
        <button className={`admindash-refresh ${scraping ? "igc-btn-busy" : ""}`} onClick={scrapeAll} disabled={scraping || !apifyConfigured} title={apifyConfigured ? "" : "Needs APIFY_API_TOKEN on the server (apify.com has a free tier)"}>{scraping ? <><span className="igc-spin igc-spin--light" />Scraping…</> : "Scrape all"}</button>
        <button className={`admindash-refresh ${analyzing ? "igc-btn-busy" : ""}`} onClick={analyzePosts} disabled={analyzing}>{analyzing ? <><span className="igc-spin igc-spin--light" />Analyzing…</> : "Analyze new posts"}</button>
      </div>
      {!apifyConfigured && <div className="igc-banner">Scraping needs an Apify account (apify.com, has a free tier) and <code>APIFY_API_TOKEN</code> set on the server. Tracking + analysis views work either way once you have data.</div>}
      <JobLog job={scrapeJob} label="Scraping" />
      <JobLog job={analyzeJob} label="Analyzing" />

      <div className="ce-list" style={{ marginTop: 10 }}>
        {competitors.length === 0 ? <div className="admindash-empty">No competitors tracked yet.</div> : competitors.map((c) => (
          <div key={c._id}>
            <div className="ce-card ce-card--static">
              <div className="ce-card-main">
                <div className="ce-card-title">@{c.handle}</div>
                <div className="muted">
                  {c.totalVideos > 0 ? <>{compact(c.avgViews)} avg views · {c.totalVideos} posts{c.category ? ` · ${c.category}` : ""}</> : "Not scraped yet"}
                  {c.lastScrapedAt && <> · scraped {rel(c.lastScrapedAt)}</>}
                </div>
                {c.lastScrapeError && <div className="ce-scrape-err">⚠ {c.lastScrapeError}</div>}
              </div>
              <button className="igc-chip" onClick={() => toggleActive(c)}>{c.isActive ? "Active" : "Paused"}</button>
              <button className="ce-x" onClick={() => remove(c._id)}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      {analysis && !analysis.empty && (
        <>
          <div className="ce-panel-title" style={{ marginTop: 18 }}>You vs competitors</div>
          <table className="ce-table"><thead><tr><th>Handle</th><th>Posts</th><th>Avg views</th><th>Outlier rate</th></tr></thead>
            <tbody>
              <tr className="ce-you-row"><td>You</td><td>{num(analysis.you.videos)}</td><td>{compact(analysis.you.avgViews)}</td><td>{analysis.you.outlierRate}%</td></tr>
              {analysis.overview.map((o) => <tr key={o.handle}><td>@{o.handle}</td><td>{num(o.videos)}</td><td>{compact(o.avgViews)}</td><td>{o.outlierRate}%</td></tr>)}
            </tbody>
          </table>
          {analysis.perCompetitor.map((c) => c.topHooks.length > 0 && (
            <div key={c.handle} style={{ marginTop: 14 }}>
              <div className="ce-panel-title">@{c.handle} <span className="muted">— {compact(c.avgViews)} avg views</span></div>
              {(!!c.bestStructures?.length || !!c.bestTopics?.length) && (
                <div className="muted ce-best-line">
                  {!!c.bestStructures?.length && <>Best hook structures: {c.bestStructures.slice(0, 3).map((s) => `${s.name} (${compact(s.avgViews)} avg)`).join(", ")}<br /></>}
                  {!!c.bestTopics?.length && <>Best topics: {c.bestTopics.slice(0, 3).map((t) => t.name).join(", ")}</>}
                </div>
              )}
              <div className="ce-hooklist">{c.topHooks.map((h, i) => (
                <div key={i} className="ce-hookrow">
                  <div className="ce-hook-text"><p>"{h.spokenHook}"</p>{h.hookFramework && <div className="muted ce-hook-fw">{h.hookFramework}</div>}</div>
                  <div className="ce-hook-meta"><span className="igc-badge">{h.hookStructure || "—"}</span><span className="muted">{compact(h.views)} views · {h.outlierScore}×</span></div>
                  {h.url && <a className="igc-chip" href={h.url} target="_blank" rel="noreferrer">View ↗</a>}
                  {onScript && <button className="igc-chip" onClick={() => scriptForClipbait(h, i)} disabled={adapting === i}>{adapting === i ? "Adapting…" : "Script it for Clipbait →"}</button>}
                </div>
              ))}</div>
            </div>
          ))}
        </>
      )}
      {analysis?.empty && <div className="igc-banner">{analysis.note}</div>}
    </div>
  );
}

/* ───────────────────────── Prompt (free text + / commands) ───────────────────────── */

// Renders whatever a command's `result` shape happens to be, reusing existing display patterns per
// command rather than a generic JSON dump — same visual language as each command's own tab.
function AssistantResult({ command, result }) {
  if (command === "help") {
    return <div className="ce-hooklist">{result.commands.map((c) => (
      <div key={c.name} className="ce-hookrow"><div className="ce-hook-text"><p><code>{c.usage}</code></p><div className="muted ce-hook-fw">{c.desc}</div></div></div>
    ))}</div>;
  }
  if (command === "hooks") {
    return <div className="ce-hooklist">{result.hooks.map((h, i) => (
      <div key={i} className="ce-hookrow">
        <div className="ce-hook-text"><p>"{h.hook}"</p>{h.framework && <div className="muted ce-hook-fw">{h.framework}</div>}</div>
        <div className="ce-hook-meta"><span className="igc-badge">{h.source}</span>{h.originalViews != null && <span className="muted">{compact(h.originalViews)} original views</span>}</div>
      </div>
    ))}</div>;
  }
  if (command === "script" || command === "clipbait") return <FilmingCard result={result} />;
  if (command === "ideate") {
    return <div className="ce-hooklist">{result.variations.map((v, i) => (
      <div key={i} className="ce-hookrow"><div className="ce-hook-text"><div className="ce-card-title">{v.title}</div><div className="muted">{v.framing} · {v.contentType} · {v.format} · ~{v.durationSeconds}s</div><p>{v.description}</p></div></div>
    ))}</div>;
  }
  if (command === "research") {
    return result.topics?.length
      ? <div className="ce-hooklist">{result.topics.map((t, i) => <div key={i} className="ce-hookrow"><div className="ce-hook-text"><div className="ce-card-title">{t.title}</div><p>{t.body}</p></div></div>)}</div>
      : <div className="muted">No trending topics found.</div>;
  }
  if (command === "brief") {
    const o = result.overview, r = result.rankings;
    return <div className="muted">
      {o.totalPosts} posts analyzed · {compact(o.avgViews)} avg views · {o.outlierRate}% outlier rate<br />
      {r.bestHookStructure && <>Best hook structure: {r.bestHookStructure.name} ({r.bestHookStructure.outlierRate}% outlier rate)<br /></>}
      {r.bestTopics?.length && <>Best topics: {r.bestTopics?.map?.((t) => t.name).join(", ")}</>}
    </div>;
  }
  if (command === "competitors") {
    return <table className="ce-table"><thead><tr><th>Handle</th><th>Posts</th><th>Avg views</th><th>Outlier rate</th></tr></thead>
      <tbody>
        <tr className="ce-you-row"><td>You</td><td>{num(result.you.videos)}</td><td>{compact(result.you.avgViews)}</td><td>{result.you.outlierRate}%</td></tr>
        {result.overview.map((o) => <tr key={o.handle}><td>@{o.handle}</td><td>{num(o.videos)}</td><td>{compact(o.avgViews)}</td><td>{o.outlierRate}%</td></tr>)}
      </tbody>
    </table>;
  }
  return <pre className="ce-fc-wrap" style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(result, null, 2)}</pre>;
}

function PromptView() {
  const [input, setInput] = useState(""); const [history, setHistory] = useState([]); const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(null);
  useEffect(() => { axiosInstance.post("/admin/content-engine/assistant", { input: "/help" }).then((r) => setHelp(r.data.result.commands)).catch(() => {}); }, []);

  const run = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true); setInput("");
    try {
      const r = (await axiosInstance.post("/admin/content-engine/assistant", { input: text })).data;
      setHistory((h) => [{ input: text, ...r }, ...h]);
    } catch (e) {
      setHistory((h) => [{ input: text, type: "error", error: errMsg(e, "Something went wrong") }, ...h]);
    }
    setBusy(false);
  };

  return (
    <div>
      <div className="ce-panel">
        <div className="ce-panel-title">Prompt — free text or a / command</div>
        {help && <div className="muted ce-best-line">{help.map((c) => <code key={c.name} style={{ marginRight: 10 }}>{c.name}</code>)}</div>}
        <div className="ce-row-flex">
          <input
            className="ce-input ce-input--full" placeholder="Ask for ideas, or type a / command (e.g. /hooks growing on Twitch)"
            value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
          />
          <button className={`admindash-refresh ${busy ? "igc-btn-busy" : ""}`} onClick={run} disabled={busy || !input.trim()}>{busy ? <span className="igc-spin igc-spin--light" /> : "Run"}</button>
        </div>
      </div>

      {history.map((h, i) => (
        <div key={i} className="ce-panel" style={{ marginTop: 10 }}>
          <div className="ce-panel-title">{h.input}</div>
          {h.type === "error" ? <div className="igc-banner">{h.error}</div>
            : h.type === "chat" ? <p>{h.reply}</p>
            : <AssistantResult command={h.command} result={h.result} />}
        </div>
      ))}
    </div>
  );
}
